import React, { useState, useEffect, useRef } from 'react'
import Modal from 'react-modal'
import { getApi, postApi, deleteApi } from '../../../Services/apiService'
import { toast } from 'react-toastify'

const Notification = () => {
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null) // Track which notification's menu is open
  const menuRefs = useRef({}) // Refs for each notification menu
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkMarkingRead, setBulkMarkingRead] = useState(false)
  const [bulkArchiving, setBulkArchiving] = useState(false)
  const [readFilter, setReadFilter] = useState('all') // 'all' | 'read' | 'unread' | 'archive'
  const [sortBy, setSortBy] = useState('default') // 'default' | 'alphabetical' | 'latest'
  const [archivedNotifications, setArchivedNotifications] = useState([])
  const [archivedLoaded, setArchivedLoaded] = useState(false)

  const isArchivedView = readFilter === 'archive'

  // Format timestamp to "X mins ago" format
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return ''
    
    // Convert string timestamp to number if needed
    const timestampNum = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp
    
    const now = new Date()
    const notificationDate = new Date(timestampNum)
    
    // Check if date is valid
    if (isNaN(notificationDate.getTime())) return ''
    
    const diffInMs = now - notificationDate
    const diffInMins = Math.floor(diffInMs / 60000)
    const diffInHours = Math.floor(diffInMs / 3600000)
    const diffInDays = Math.floor(diffInMs / 86400000)
    const diffInWeeks = Math.floor(diffInDays / 7)
    const diffInMonths = Math.floor(diffInDays / 30)
    const diffInYears = Math.floor(diffInDays / 365)

    if (diffInMins < 1) return 'Just now'
    if (diffInMins < 60) return `${diffInMins} ${diffInMins === 1 ? 'min' : 'mins'} ago`
    if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`
    if (diffInDays < 7) return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`
    if (diffInWeeks < 4) return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`
    if (diffInMonths < 12) return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`
  }

  // Map an API notification to the notification card format
  const mapNotification = (notification) => {
    // Use profile_image from API if available and not empty, otherwise use dummy-profile.jpg
    const hasProfileImage = notification.profile_image &&
                           typeof notification.profile_image === 'string' &&
                           notification.profile_image.trim() !== ''
    const profileImage = hasProfileImage
      ? notification.profile_image
      : window.getPublicUrl('/assets/images/dummy-profile.jpg')

    // Use title and message directly from API response
    const title = notification.title || 'Notification'
    const body = notification.message || ''

    // is_read: 0 = unread (false), is_read: 1 = read (true)
    // Also check for isRead (future API format)
    const isRead = notification.isRead !== undefined
      ? notification.isRead === true
      : (notification.is_read === 1)

    return {
      id: notification.id,
      profileImage,
      title,
      body,
      timestamp: formatTimeAgo(notification.createdAt),
      createdAt: typeof notification.createdAt === 'string' ? parseInt(notification.createdAt, 10) : notification.createdAt,
      isRead
    }
  }

  // Fetch notifications from API. `filter` is 'all' for the live list and
  // 'archive' for the archived list, which lands in its own state bucket.
  const fetchNotifications = async (filter = 'all') => {
    const archived = filter === 'archive'
    const setList = archived ? setArchivedNotifications : setNotifications
    try {
      setLoading(true)
      setError(null)

      // Build query parameters
      const params = new URLSearchParams({
        filter, // all, read, unread, or archive
        limit: '50',
        offset: '0'
      })

      const response = await getApi(`/notifications?${params.toString()}`)

      const notificationsData = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data

      if (notificationsData && Array.isArray(notificationsData) && notificationsData.length > 0) {
        setList(notificationsData.map(mapNotification))
      } else {
        // Empty response or no data
        setList([])
      }
      if (archived) setArchivedLoaded(true)
    } catch (err) {
      console.error('Error fetching notifications:', err)
      // On error, show empty state instead of error message
      setList([])
      setError(null)
      if (archived) setArchivedLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications('all')
  }, [])

  // Archived list is fetched lazily the first time the Archive filter is opened
  useEffect(() => {
    if (isArchivedView && !archivedLoaded) {
      fetchNotifications('archive')
    }
  }, [isArchivedView, archivedLoaded])

  // Mark notification(s) as read — sends id as array, no is_read
  const markNotificationsAsRead = async (ids) => {
    if (!ids || ids.length === 0) return
    const idArray = Array.isArray(ids) ? ids : [ids]
    try {
      const response = await postApi('/read-notification', {
        ids: idArray
      })

      if (response?.statusCode === 200) {
        const markRead = (list) =>
          list.map(notif =>
            idArray.includes(notif.id) ? { ...notif, isRead: true } : notif
          )
        setNotifications(markRead)
        setArchivedNotifications(markRead)
        return true
      }
      return false
    } catch (err) {
      console.error('Error marking notifications as read:', err)
      return false
    }
  }

  // Single notification: mark as read (menu action)
  const toggleNotificationReadStatus = async (notificationId, currentIsRead) => {
    if (currentIsRead) return
    try {
      const success = await markNotificationsAsRead([notificationId])
      if (success) {
        setOpenMenuId(null)
        toast.success('Notification status updated')
      } else {
        toast.error('Failed to update notification status')
      }
    } catch (err) {
      toast.error('Failed to update notification status')
    }
  }

  const handleBulkMarkAsRead = async () => {
    if (selectedIds.length === 0) return
    try {
      setBulkMarkingRead(true)
      const success = await markNotificationsAsRead(selectedIds)
      if (success) {
        setSelectedIds([])
        toast.success('Notifications marked as read')
      } else {
        toast.error('Failed to mark notifications as read')
      }
    } catch (err) {
      toast.error('Failed to mark notifications as read')
    } finally {
      setBulkMarkingRead(false)
    }
  }

  // Archive selected notifications — sends the ids in the payload
  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return
    try {
      setBulkArchiving(true)
      const response = await postApi('/archive-notification', {
        ids: selectedIds
      })

      if (response?.statusCode === 200) {
        // Archived items leave the live list; the archived list is refetched
        // the next time the Archive filter is opened.
        setNotifications((prev) => prev.filter((n) => !selectedIds.includes(n.id)))
        setArchivedLoaded(false)
        setSelectedIds([])
        setSelectMode(false)
        toast.success('Notifications archived')
      } else {
        toast.error('Failed to archive notifications')
      }
    } catch (err) {
      console.error('Error archiving notifications:', err)
      toast.error('Failed to archive notifications')
    } finally {
      setBulkArchiving(false)
    }
  }

  const toggleSelectMode = () => {
    setSelectMode((prev) => !prev)
    if (selectMode) setSelectedIds([])
    setOpenMenuId(null)
  }

  const toggleSelectNotification = (notificationId) => {
    setSelectedIds((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    )
  }

  const toggleSelectAll = () => {
    const visibleIds = sortedNotifications.map((n) => n.id).filter((id) => id != null)
    if (visibleIds.length === 0) {
      setSelectedIds([])
      return
    }
    const allSelected = visibleIds.every((id) => selectedIds.includes(id))
    setSelectedIds(allSelected ? [] : visibleIds)
  }

  const handleBulkDelete = () => {
    if (!selectedIds.length) return
    setShowDeleteModal(true)
  }

  const performBulkDelete = async () => {
    if (!selectedIds.length) return
    try {
      setBulkDeleting(true)
      await deleteApi('/delete-notifications', {
        data: { notification_ids: selectedIds },
      })
      const dropSelected = (list) => list.filter((n) => !selectedIds.includes(n.id))
      setNotifications(dropSelected)
      setArchivedNotifications(dropSelected)
      setSelectedIds([])
      setShowDeleteModal(false)
      setSelectMode(false)
      toast.success('Notifications deleted')
    } catch (err) {
      console.error('Error deleting notifications:', err)
      toast.error('Failed to delete notifications')
    } finally {
      setBulkDeleting(false)
    }
  }

  // Handle click outside to close notification menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside all notification menus
      const clickedOutsideAllMenus = Object.values(menuRefs.current).every(
        ref => ref && !ref.contains(event.target)
      )
      
      if (clickedOutsideAllMenus) {
        setOpenMenuId(null)
      }
    }

    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuId])

  const filteredNotifications = isArchivedView
    ? archivedNotifications
    : readFilter === 'all'
      ? notifications
      : readFilter === 'read'
        ? notifications.filter((n) => n.isRead)
        : notifications.filter((n) => !n.isRead)

  // Selection never spans two filters
  const changeReadFilter = (filter) => {
    if (filter === readFilter) return
    setReadFilter(filter)
    setSelectedIds([])
  }

  // Apply sort to filtered list
  const sortedNotifications = (() => {
    if (sortBy === 'default') return filteredNotifications
    if (sortBy === 'alphabetical') {
      return [...filteredNotifications].sort((a, b) => {
        const titleA = (a.title || '').toLowerCase()
        const titleB = (b.title || '').toLowerCase()
        if (titleA !== titleB) return titleA.localeCompare(titleB)
        const bodyA = (a.body || '').toLowerCase()
        const bodyB = (b.body || '').toLowerCase()
        return bodyA.localeCompare(bodyB)
      })
    }
    if (sortBy === 'latest') {
      return [...filteredNotifications].sort((a, b) => {
        const timeA = a.createdAt != null ? Number(a.createdAt) : 0
        const timeB = b.createdAt != null ? Number(b.createdAt) : 0
        return timeB - timeA // newest first
      })
    }
    return filteredNotifications
  })()

  const allNotificationIds =
    sortedNotifications.map((n) => n.id).filter((id) => id != null) || []
  const isAllSelected =
    allNotificationIds.length > 0 &&
    allNotificationIds.every((id) => selectedIds.includes(id))

  return (
    <div className='notification right-with-header'>
      <div className='notification-page-header'>
        <div className='notification-header-right'>
          {/* Read filters and sort chips share one horizontally scrollable row */}
          <div className='notification-chip-row'>
            <div className='notification-filter-toggle' aria-disabled={loading}>
              <button
                type='button'
                className={`notification-filter-btn ${readFilter === 'all' ? 'active' : ''}`}
                onClick={() => changeReadFilter('all')}
                disabled={loading}
              >
                All
              </button>
              <button
                type='button'
                className={`notification-filter-btn ${readFilter === 'unread' ? 'active' : ''}`}
                onClick={() => changeReadFilter('unread')}
                disabled={loading}
              >
                Unread
              </button>
              <button
                type='button'
                className={`notification-filter-btn ${readFilter === 'read' ? 'active' : ''}`}
                onClick={() => changeReadFilter('read')}
                disabled={loading}
              >
                Read
              </button>
              <button
                type='button'
                className={`notification-filter-btn ${readFilter === 'archive' ? 'active' : ''}`}
                onClick={() => changeReadFilter('archive')}
                disabled={loading}
              >
                Archive
              </button>
            </div>
            <div className='notification-sort-toggle'>
              <button
                type='button'
                className={`notification-sort-btn ${sortBy === 'default' ? 'active' : ''}`}
                onClick={() => setSortBy('default')}
                disabled={loading}
              >
                Default
              </button>
              <button
                type='button'
                className={`notification-sort-btn ${sortBy === 'alphabetical' ? 'active' : ''}`}
                onClick={() => setSortBy('alphabetical')}
                disabled={loading}
              >
                Alphabetical
              </button>
              <button
                type='button'
                className={`notification-sort-btn ${sortBy === 'latest' ? 'active' : ''}`}
                onClick={() => setSortBy('latest')}
                disabled={loading}
              >
                Latest first
              </button>
              {sortBy !== 'default' && (
                <button
                  type='button'
                  className='notification-reset-sort-btn'
                  onClick={() => setSortBy('default')}
                  disabled={loading}
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          <div className='notification-toolbar'>
            <button
              type='button'
              className='notification-select-btn'
              onClick={toggleSelectMode}
              disabled={loading}
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>
            {selectMode && filteredNotifications.length > 0 && (
              <>
                <label className='notification-select-all'>
                  <input
                    type='checkbox'
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                  Select All
                </label>
                <button
                  type='button'
                  className='notification-mark-read-toolbar-btn'
                  onClick={handleBulkMarkAsRead}
                  disabled={selectedIds.length === 0 || bulkMarkingRead}
                  aria-label='Mark selected as read'
                >
                  {bulkMarkingRead ? 'Updating...' : 'Mark as Read'}
                </button>
                {!isArchivedView && (
                  <button
                    type='button'
                    className='notification-archive-toolbar-btn'
                    onClick={handleBulkArchive}
                    disabled={selectedIds.length === 0 || bulkArchiving}
                    aria-label='Archive selected'
                  >
                    {bulkArchiving ? 'Archiving...' : 'Archive'}
                  </button>
                )}
                <button
                  type='button'
                  className='notification-delete-btn'
                  onClick={handleBulkDelete}
                  disabled={selectedIds.length === 0}
                  aria-label='Delete selected'
                >
                  <img
                    src={window.getPublicUrl('/assets/images/deleteEvent.svg')}
                    alt='Delete selected'
                  />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className='notification-cards-container'>
        {loading ? (
          <div className='notification-loading'>
            <p>Loading notifications...</p>
          </div>
        ) : sortedNotifications.length === 0 ? (
          <div className='notification-empty'>
            <p>
              {isArchivedView
                ? 'No archived notifications'
                : notifications.length === 0
                  ? 'No notification yet'
                  : `No ${readFilter} notifications`}
            </p>
          </div>
        ) : (
          sortedNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-card ${!notification.isRead ? 'notification-unread' : ''}`}
            >
              <div className='notification-card-content'>
                {selectMode && (
                  <div className='notification-card-checkbox'>
                    <input
                      type='checkbox'
                      checked={selectedIds.includes(notification.id)}
                      onChange={() => toggleSelectNotification(notification.id)}
                    />
                  </div>
                )}
                <div className='notification-text-content'>
                  <div className='notification-header'>
                    <h4 className='notification-title'>{notification.title}</h4>
                    {!notification.isRead && !selectMode && (
                      <div className='notification-menu-wrapper' ref={(el) => (menuRefs.current[notification.id] = el)}>
                        <button
                          className='notification-menu-btn'
                          aria-label='More options'
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenMenuId(openMenuId === notification.id ? null : notification.id)
                          }}
                        >
                          <svg width='4' height='16' viewBox='0 0 4 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <circle cx='2' cy='2' r='1.5' fill='#666' />
                            <circle cx='2' cy='8' r='1.5' fill='#666' />
                            <circle cx='2' cy='14' r='1.5' fill='#666' />
                          </svg>
                        </button>
                        {openMenuId === notification.id && (
                          <div className='notification-menu-dropdown'>
                            <button
                              type='button'
                              className='notification-mark-read-btn'
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleNotificationReadStatus(notification.id, notification.isRead)
                              }}
                            >
                              Mark as Read
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <p className='notification-body'>{notification.body}</p>
                  <span className='notification-timestamp'>{notification.timestamp}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bulk delete confirmation modal */}
      <Modal
        isOpen={showDeleteModal}
        onRequestClose={() => !bulkDeleting && setShowDeleteModal(false)}
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            border: 'none',
            background: 'transparent',
            padding: '0',
            maxWidth: '420px',
            width: '100%',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          },
        }}
        portalClassName='logOut'
        contentLabel='Delete Notifications Confirmation'
        ariaHideApp={false}
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <div>
              <span>
                <img src={window.getPublicUrl('/assets/images/logout.svg')} alt="" />
              </span>
              <button
                type='button'
                className='close'
                onClick={() => !bulkDeleting && setShowDeleteModal(false)}
                aria-label='Close'
              >
                <img src={window.getPublicUrl('/assets/images/close.svg')} alt="" />
              </button>
            </div>
          </div>
          <div className='modal-body'>
            <h5 className='modal-body-hading'>Delete Notifications</h5>
            <p className='modal-peragrapgh'>
              Do you want to delete the selected notification(s)? This action cannot be undone.
            </p>
          </div>
          <div
            className='modal-footer'
            style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}
          >
            <button
              type='button'
              className='btn btn-cancel'
              onClick={() => !bulkDeleting && setShowDeleteModal(false)}
              disabled={bulkDeleting}
              style={{ width: '170px', height: '48px' }}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-block'
              onClick={performBulkDelete}
              disabled={bulkDeleting}
              style={{ width: '170px', height: '48px' }}
            >
              {bulkDeleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Notification